/**
 * Normalizes the several error envelopes this app can receive.
 *
 * The BFF (wwdb-wwg-store-api) and the microservices behind it
 * (func-bsm-api, func-crm-api, func-membership-api) do not agree on a single
 * error shape, and none of them emit machine-readable error codes. The shapes
 * observed are:
 *
 *   { error: { message: "..." } }                     microservices, BFF 404/409 pass-through
 *   { error: "CRM request failed: 406" }              BFF standard catch
 *   { error: "...", details: { error: { message } } } BFF CRM endpoints
 *   { error: { message: { code, message } } }         structured business outcomes
 *   { error: { message: [ ...zod issues ] } }         func-membership-api validation
 *   { message: "..." }                                func-membership-api direct context.res
 */

function firstString(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

// Walks the envelopes above to find the innermost human-readable text plus any
// structured { code, message } detail that came with it.
function extractFromPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return { serverMessage: null, code: null, detail: null };
  }

  // details.error is checked before details itself: on BFF CRM endpoints the
  // top-level `error` is the generic developer label and the useful text sits
  // under details.error. A bare `details` is ranked last of the three because
  // it is often the raw upstream error object.
  const candidates = [
    payload.details?.error,
    payload.error,
    payload.details,
    payload,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return { serverMessage: candidate.trim(), code: null, detail: null };
    }

    if (!candidate || typeof candidate !== "object") {
      continue;
    }

    const inner = candidate.message;

    if (typeof inner === "string" && inner.trim()) {
      return {
        serverMessage: inner.trim(),
        code: firstString(candidate.code),
        detail: candidate.code ? candidate : null,
      };
    }

    // Nested structured form: { error: { message: { code, message } } }
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
      const nested = firstString(inner.message);
      if (nested) {
        return {
          serverMessage: nested,
          code: firstString(inner.code),
          detail: inner,
        };
      }
    }

    // Zod issue arrays carry no message we can safely show, but the detail is
    // worth keeping for logs.
    if (Array.isArray(inner)) {
      return { serverMessage: null, code: null, detail: inner };
    }
  }

  return { serverMessage: null, code: null, detail: null };
}

export function createApiError(fallbackLabel, status, payload) {
  const { serverMessage, code, detail } = extractFromPayload(payload);

  // message stays the developer-facing string the app has always thrown, so
  // existing consumers and logs are unaffected. User-facing copy is derived
  // separately in errorMessages.js, which decides whether serverMessage is
  // safe to display.
  const error = new Error(`${fallbackLabel}: ${status}`);
  error.status = status;
  error.serverMessage = serverMessage;
  if (code) error.code = code;
  if (detail) error.detail = detail;

  return error;
}

/**
 * Builds a normalized Error from a failed fetch Response. Consumes the body,
 * so call this only on a response you are not going to read again.
 *
 * @param {Response} res - The non-ok fetch response.
 * @param {string} fallbackLabel - Developer-facing label, e.g. "Cart fetch failed".
 */
export async function parseApiError(res, fallbackLabel) {
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  return createApiError(fallbackLabel, res.status, payload);
}
